import { useEffect, useState } from 'react';

import { rememberPointer, subscribeToInk, type InkBurst } from '../hooks/useInk';

// The circle has to reach the far corner of the viewport, whatever screen it
// lands on. A fixed scale factor was the whole reason the effect looked timid
// on a desktop: 14 px grown 58x covers a phone and barely a third of a 27".
const reach = (x: number, y: number): number => {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return Math.max(
    Math.hypot(x, y),
    Math.hypot(w - x, y),
    Math.hypot(x, h - y),
    Math.hypot(w - x, h - y),
  );
};

/**
 * The one place ink is drawn. Mounted once in AppShell, above everything and
 * deaf to the pointer, so a spreading colour can never swallow a tap.
 *
 * The animation itself lives in index.css (.ink-core / .ink-halo): a CSS
 * animation starts from the correct resting state on the first painted frame,
 * which a Web Animations call cannot promise.
 */
export const InkLayer = () => {
  const [burst, setBurst] = useState<InkBurst | null>(null);

  useEffect(() => subscribeToInk(setBurst), []);

  // One listener for the whole app: every burst starts where the last press
  // landed, so call sites never have to carry the event to their onSuccess.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => rememberPointer(event.clientX, event.clientY);
    document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
    return () => document.removeEventListener('pointerdown', onPointerDown, { capture: true });
  }, []);

  // Belt and braces: the halo's animationend removes the burst, and this makes
  // sure a burst can never get stuck on screen if that event is missed.
  useEffect(() => {
    if (!burst) {
      return;
    }
    const timer = window.setTimeout(
      () => setBurst((current) => (current?.id === burst.id ? null : current)),
      2200,
    );
    return () => window.clearTimeout(timer);
  }, [burst]);

  if (!burst) {
    return null;
  }

  const size = reach(burst.x, burst.y) * 2;
  const shared = { left: burst.x, top: burst.y, width: size, height: size };

  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[60] overflow-hidden">
      <span
        key={`halo-${burst.id}`}
        className="ink-burst ink-halo"
        style={{
          ...shared,
          background: `radial-gradient(circle, ${burst.color}80 0%, ${burst.color}40 55%, ${burst.color}00 100%)`,
        }}
        onAnimationEnd={() => setBurst((current) => (current?.id === burst.id ? null : current))}
      />
      <span
        key={`core-${burst.id}`}
        className="ink-burst ink-core"
        style={{
          ...shared,
          background: `radial-gradient(circle, ${burst.color} 0%, ${burst.color} 62%, ${burst.color}00 100%)`,
        }}
      />
    </div>
  );
};
